import { LitElement, html, css, ifDefined, classMap } from '@hh/global.js'

class HHCustomSelect extends LitElement {
  static properties = {
    options: {
      type: Object,
    },
    name: {
      type: String,
    },
    active: {
      type: String,
    },
    lang: {
      type: Boolean,
    },
    position: {
      type: String,
    },
    label: {
      type: String,
    },
    _selected: {
      state: true,
    },
    _active: {
      state: true,
    },
    open: {
      type: Boolean,
      reflect: true,
    },
    error: {
      type: Boolean,
      reflect: true,
    },
    disabled: {
      type: Boolean,
      reflect: true,
    },
    defaultUnselected: {
      type: Boolean,
      reflect: true,
      attribute: 'default-unselected',
    },
    labelVisuallyHidden: {
      type: Boolean,
      reflect: true,
      attribute: 'label-visually-hidden',
    },
    selected: {
      type: Boolean,
      reflect: true,
    },
    _currentOption: {
      state: true,
    },
    labelInline: {
      type: Boolean,
      reflect: false,
      attribute: 'label-inline',
    },
  }

  constructor() {
    super()
    this.handleBodyClick = this.handleBodyClick.bind(this)
    this.uuid = Math.random() * (1000000 - 1) + 1
    this.position = 'bottom'
    this._active = false
  }

  connectedCallback() {
    super.connectedCallback()
    this._selected = this.active
    document.body.addEventListener('click', this.handleBodyClick)
    document.addEventListener('focusin', this.handleDocumentFocusIn.bind(this))
    this.addEventListener('keydown', this.handleKeydown.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.body.addEventListener('click', this.handleBodyClick)
    document.removeEventListener('focusin', this.handleFocusIn)
    this.removeEventListener('keydown', this.handleKeydown)
  }

  handleBodyClick(e) {
    const path = e.composedPath()
    if (
      path.some((node) => {
        return node === this
      })
    ) {
      return true
    }

    this._active && this.toggle()
  }

  toggle(e = false) {
    if (this.disabled) return
    if (e && e.type === 'keyup' && !~['ESCAPE'].indexOf(e.code.toUpperCase())) {
      return true
    }

    this._active = !this._active
    this.open = this._active
  }

  selectValue(e, value) {
    e.preventDefault()
    this._selected = value
    ;(this.querySelector('[slot="input"]') || {}).value = value
    this.dispatchEvent(
      new CustomEvent('change', {
        bubbles: true,
        composed: true,
        detail: {
          value,
        },
      })
    )
    this.toggle()
  }

  get selectedLabel() {
    const option = this.options.find(
      (option) => option.value === this._selected
    )

    if (!option && this.defaultUnselected) return ''

    if (!option) return 'Invalid selected option'
    this.selected = true
    return option.label
  }

  handleKeydown(e) {
    if (e.key.toUpperCase() === 'ESCAPE') {
      e.preventDefault()
      e.stopPropagation()
      this._active = false
      this.open = false
      this.button.focus({ preventScroll: true })
    }

    if (e.code.toUpperCase() == 'SPACE' || e.key === ' ') {
      if (this._active) {
        e.preventDefault()
        e.stopPropagation()
      }

      this.selectValue(e, this._currentOption.dataset.optionValue)
      this.button.focus({ preventScroll: true })
      return
    }

    if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
      const allOptions = this._getAllOptions()
      const currentIndex = allOptions.indexOf(this._currentOption)
      let newIndex = Math.max(0, currentIndex)

      // Prevent scrolling
      e.preventDefault()

      if (!this._active) {
        this.toggle(e)
        if (this._currentOption) {
          this._currentOption.focus({ preventScroll: true })
          return
        }
      }

      if (e.key === 'ArrowDown') {
        newIndex = currentIndex + 1
      } else if (e.key === 'ArrowUp') {
        newIndex = currentIndex - 1
      }

      this._setCurrentOption(allOptions[newIndex])
    }
  }

  _getAllOptions() {
    return [...this.renderRoot?.querySelectorAll('.link')]
  }

  _getFirstOption() {
    return this.renderRoot?.querySelector('.link')
  }

  _setCurrentOption(option) {
    const allOptions = this._getAllOptions()

    // Clear selection
    allOptions.forEach((el) => {
      el.tabIndex = -1
    })

    // Select the target option
    if (option) {
      this._currentOption = option
      option.tabIndex = 0
      setTimeout(() => {
        option.focus()
      }, 100)
    }
  }

  handleDocumentFocusIn(e) {
    // Close when focusing out of the select
    const path = e.composedPath()
    if (this && !path.includes(this)) {
      this._active = false
      this.open = false
    }
  }

  handleEnterKey(e) {
    if (e.key !== 'Enter') return
    this.button.focus({ preventScroll: true })
  }

  sanitize(value) {
    return value.replace('&amp;', '&')
  }

  get button() {
    return this.renderRoot?.querySelector('button') ?? null
  }

  render() {
    const allOptions = this._getAllOptions()
    const currentIndex = allOptions.indexOf(this._currentOption)

    return html`<div>
      <div class="container">
        <div class="label-wrapper" @click=${this.toggle}>
          <div
            id="${this.uuid}-label"
            class=${classMap({
              'visually-hidden': this.labelVisuallyHidden,
              hidden: this.labelInline,
            })}
          >
            <slot name="title"></slot>
          </div>
          <button
            type="button"
            class="button"
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded="${this._active}"
            aria-controls="${this.uuid}-list"
            aria-describedby="${this.uuid}-label"
            aria-labelledby="${this.uuid}-label ${this.uuid}-selected-label"
          >
            <span
              class=${classMap({
                button__label: true,
                inline: this.labelInline,
              })}
              ><span
                class=${classMap({
                  inline__label: true,
                  hidden: !this.labelInline,
                })}
                >${this.label}</span
              >
              <span
                class=${classMap({
                  selected__label: this.labelInline,
                })}
                id="${this.uuid}-selected-label"
                >${this.selectedLabel}</span
              ></span
            >
            <slot name="icon"></slot>
          </button>
        </div>

        <slot name="input"></slot>
        <ul
          aria-labelledby="${this.uuid}-label"
          id="${this.uuid}-list"
          class="${classMap({
            list: true,
            'is-active': this._active,
            'position-top': this.position === 'top',
          })}"
          @keyup="${this.toggle}"
          aria-hidden="${!this._active}"
          role="listbox"
          aria-activedescendant="${this.uuid}-list-${currentIndex}"
        >
          ${this.options.map((option, idx) => {
            this.lang && (option.lang = option.value)
            return html`<li
              class="${classMap({
                option: true,
                'is-active': option.value === this._selected,
              })}"
              role="option"
              aria-selected="${option.value === this._selected}"
              id="${this.uuid}-list-${idx}"
            >
              <a
                @click="${(e) => this.selectValue(e, option.value)}"
                @keyup="${this.handleEnterKey}"
                data-option-value="${option.value}"
                class="link"
                href="#"
                hreflang="${ifDefined(option.lang)}"
                lang="${ifDefined(option.lang)}"
              >
                ${this.sanitize(option.label)}
              </a>
            </li>`
          })}
        </ul>
      </div>
      <slot name="error"></slot>
      <slot name="hint"></slot>
    </div>`
  }

  static styles = css`
    :host {
      position: relative;
      display: block;
      height: var(--hh-height, 50px);
      background: var(--hh-input-background, transparent);
      border: var(--hh-border-size, 1px) solid var(--hh-border-color, #707070);
      border-radius: var(--hh-border-radius, 0);
    }

    .visually-hidden {
      position: absolute !important;
      overflow: hidden;
      width: 1px;
      height: 1px;
      margin: -1px;
      padding: 0;
      border: 0;
      clip: rect(0 0 0 0);
      word-wrap: normal !important;
    }

    .hidden {
      display: none;
    }

    .container {
      position: relative;
    }

    .button {
      align-items: center;
      cursor: pointer;
      display: flex;
      font: var(--hh-font);
      height: var(--hh-height, 50px);
      justify-content: var(--hh-button-justify, space-between);
      padding: var(--hh-padding, 10px 20px);
      width: 100%;
      background: none;
      border: none;
      color: var(--hh-color, var(--hh-color-night))
    }

    :host(:hover) {
      background: var(--hh-input-background-hover, #f1f1f1);
      cursor: pointer;
      border-color: var(--hh-border-color-hover, #707070);
    }

    :host(:not([disabled]):hover) ::slotted([slot='title']) {
      color: var(--hh-color, #000);
    }

    .button__label {
      position: relative;
      top: 8px;
    }

    .button__label.inline {
      top: 2px;
    }

    .selected__label {
      display: var(--hh-selected-label-display, inline);
    }

    .inline__label:after {
      content: var(--hh-inline-label-after, '');
    }

    ::slotted([slot='button']),
    ::slotted([slot='icon']) {
      align-items: center;
      display: flex;
      height: 100%;
    }

    ::slotted([slot='icon']) {
      margin-left: var(--hh-select-margin-left, 30px);
      width: var(--hh-select-icon-size, 10px);
      transition: transform var(--hh-duration-default, 300ms);
    }

    :host([open]) ::slotted([slot='icon']) {
      transform: rotate(180deg);
      transition: transform var(--hh-duration-default, 300ms);
    }

    :host([open]) {
      background: var(--hh-background-open, transparent);
      border-color: var(--hh-border-color-hover, #707070);
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
    }

    :host([disabled]) {
      background: var(--hh-disabled-background, transparent);
      border: var(--hh-border-size, 1px) solid var(--hh-disabled-color, #dddad4);
    }

    :host(:focus:not(:focus-visible)) {
      border: var(--hh-border-size, 1px) solid
        var(--hh-border-color-hover, #707070) !important;
    }

    :host([disabled]) .button,
    :host([disabled]) ::slotted([slot='title']) {
      color: var(--hh-disabled-color, #dddad4);
      cursor: not-allowed;
    }

    ::slotted([slot='title']) {
      font: var(--hh-font);
      color: var(--hh-hint-color);
      width: 100%;
      position: absolute;
      transform: translate(20px, 17px);
      transition: transform var(--hh-duration-default, 300ms),
        font-size var(--hh-duration-default, 300ms);
    }

    :host([selected]) ::slotted([slot='title']) {
      font: var(--hh-hint-font);
      transform: translate(20px, 10px);
    }

    .list {
      font: var(--hh-font);
      background-color: var(--hh-color-reverse);
      border: var(--hh-border-size, 1px) solid var(--hh-border-color, #f1f1f1);
      display: block;
      margin: var(--hh-list-margin, 0);
      max-height: var(--hh-options-max-height, 19rem);
      max-width: var(--hh-options-max-width, 22rem);
      min-height: var(--hh-options-min-height, 8.2rem);
      min-width: var(--hh-options-min-width, 12rem);
      opacity: 0;
      overflow-y: auto;
      padding-left: 0;
      position: absolute;
      transition: visibility var(--hh-duration-default, 300ms)
          var(--hh-duration-default, 300ms),
        opacity var(--hh-duration-default, 300ms),
        transform var(--hh-duration-default, 300ms);
      visibility: hidden;
      width: 100%;
      z-index: 1;
    }

    .list.position-top {
      bottom: 100%;
    }

    .list.is-active {
      opacity: 1;
      transform: translate(-1px, var(--hh-list-offset, 0));
      transition: transform var(--hh-duration-default, 300ms) 1ms,
        opacity var(--hh-duration-default, 300ms) 1ms, visibility 1ms;
      visibility: visible;
    }

    .option {
      list-style: none;
      padding: 0;
      height: var(--hh-options-height, 50px);
      border-top: var(--hh-border-size, 0.5px) solid
        var(--hh-border-color, #f1f1f1);
      border-bottom: var(--hh-border-size, 0.5px) solid
        var(--hh-border-color, #f1f1f1);
      transition: background-color var(--hh-duration-default, 300ms);
      box-sizing: border-box;
    }

    .option.is-active {
      opacity: var(--hh-options-opacity-active, 0.5);
    }

    .link {
      color: var(--hh-link-color, inherit);
      display: block;
      padding: var(--hh-link-padding, 17px 20px 10px);
      text-decoration: none;
      height: calc(100% - 30px);
      margin: var(--hh-link-margin, 2px);
      transition: background-color var(--hh-duration-default, 300ms);
    }

    .link:hover,
    .option:has(.link:hover),
    .option.is-active {
      background-color: var(--hh-link-background-hover, transparent);
      color: var(--hh-color, #000);
    }
  `
}

customElements.define('hh-custom-select', HHCustomSelect)
